import React, { useState } from 'react';
import { loadCompanies, exportData } from '../../../actions/companyActions';
import { Table, getVisibleColumns } from '../../common/Table';
import { hexToRgbA } from '../../../util/formatter';
import Loading from '../../common/Loading';
import KwPanel from '../../common/KwPanel';
import { userIsAny } from '../../auth/authUtils';
import userTypes from '../../../constants/userTypes';
import CompaniesListColumnsConfig from './CompaniesListColumnsConfig';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
  getTableSettingsDefaults,
  useTableSettings,
} from '../../../hooks/useTableSettings';
import { useAppSelector } from '../../../hooks';

function getAllCompanyColumns(currentUser: any) {
  let columns = [
    'companyName',
    'companyTypeName',
    'isProformaInvoicing',
    'transactionLimit',
    'globalTransactionLimit',
    'requestsOpen',
    'requestsComplete',
    'keysRequested',
  ];
  if (
    userIsAny(currentUser, [
      userTypes.ADMIN,
      userTypes.APPROVER,
      userTypes.UPLOADER,
    ])
  ) {
    columns = [...columns, 'requestActivity', 'keyActivity'];
  }
  return columns;
}

export interface CompaniesListProps {
  reload?: any;
  companyTypes?: any[];
}

function CompaniesList({ reload, companyTypes }: CompaniesListProps) {
  const currentUser = useAppSelector((state) => state.auth);
  const allColumns = getAllCompanyColumns(currentUser);
  const defaultVisibleColumns = allColumns.filter(
    (x) => x !== 'keyActivity' && x !== 'requestActivity'
  );
  const [companies, setCompanies] = useState<any>();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const { t } = useTranslation();

  const {
    tableSettings,
    handleSetTableSettings: setTableSettings,
    filters,
    filterChange,
    isLoading,
  } = useTableSettings(
    'companylist-settings',
    getTableSettingsDefaults({
      defaultSort: 'companyName',
      defaultVisibleColumns,
    }),
    null,
    loadCompanies,
    setCompanies,
    reload,
    t('Failed to load companies')
  );

  const columnsConfig = CompaniesListColumnsConfig({
    params,
    filterChange,
    companyTypes: companyTypes || [],
  });

  const visibleColumns = tableSettings.columns ?? defaultVisibleColumns;
  const columns = getVisibleColumns(visibleColumns, columnsConfig);

  return companies ? (
    <KwPanel className="mb-4">
      <Table
        columns={columns}
        allColumns={allColumns}
        defaultVisibleColumns={defaultVisibleColumns}
        rows={companies.data.map((x: any) => {
          return { Key: x.id, ...x };
        })}
        totalRowCount={companies.totalRowCount}
        filteredRowCount={companies.filteredRowCount}
        tableSettings={tableSettings}
        setTableSettings={setTableSettings}
        isLoading={isLoading}
        filters={filters}
        filterChange={filterChange}
        trStyle={(row: any) => {
          return {
            backgroundColor: `${hexToRgbA(row.colourHex, 0.0375)}`,
          };
        }}
        exportData={exportData}
        scopes={companies.scopes}
      />
    </KwPanel>
  ) : (
    <Loading></Loading>
  );
}

export default CompaniesList;
